'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { isRenderRoom } from '@/components/quiz/kit/carousel/render-protocol';
import { LogOut, Monitor, Moon, Store, Sun } from 'lucide-react';
import { tw } from '@tw';
import { PAGE_WIDTH } from '@/components/layout/PageFrame';
import { UpdateAvailableButton } from '@/components/layout/UpdateAvailableButton';
import {
  sectionsFor,
  isShortcutHere,
  shortcutsFor,
  SHORTCUT_FACE_PX,
  isNavItemReselect,
  handleNavReselect,
} from '@/components/nav/console-tabs';
import { BAR_CAPSULE, segmentPill } from '@/components/ui/bar';
import { signOut } from '@/lib/auth/signOut';
import { useOwnerMode } from '@/lib/stores/owner-store';
import { cycleTheme, syncTheme } from '@/lib/site-theme';
import { AccountMenu, MenuGlyph, MenuRow, MENU_BAR_TOP } from './AccountMenu';
import { AskPill } from './AskPill';
import { SignInPill } from './SignInPill';
import { useBarAsk } from '@/lib/stores/bar-ask.store';
import type { LastCreator } from '@/lib/types/profile';

export interface SiteBarProps {
  /** The viewer's own storefront, when they have one. */
  ownPage?: string | null;
  /** Their portrait and video, which the pill to that page wears. */
  signedIn?: boolean;
  /** Enrolled ambassador — lists the Ambassador section. Not a permission: the
   *  page is reachable by anyone who types the URL. */
  isAmbassador?: boolean;
  /** Spendable balance, which the Wallet tab wears instead of its name. */
  wallet?: { amount: number; currency: string } | null;
  /** The creator a client's capsule points back to. */
  lastCreator?: LastCreator | null;
}

/**
 * THE navigation — one bar, every route, no exceptions. The same shape at
 * every width: a burger holding everything, and the shortcut capsule beside it.
 * `shortcutsFor` owns what is in the capsule; a signed-out visitor has no home
 * to be sent to and gets the Sign in pill in that slot instead.
 *
 * The saturated fill belongs to that pill. It used to light up the item you
 * were already standing on, which is the quietest information the bar carries.
 *
 * The sheet holds SECTIONS, which is why a signed-out visitor has no burger at
 * all rather than one opening onto a single row. Theme is a row of the sheet
 * (2026-09-14): a viewer without a sheet follows their device, which is what
 * a page does before anyone chooses.
 *
 * A client is NOT a special case — a viewer with a short list. Their People
 * page and a provider's Referrals are different pages with different content,
 * not one page relabelled.
 *
 * Everything is the bar's own material, theme-following like the rest of the
 * product: `--bar-raise` surfaces with the bar shadow and no border, items as
 * quiet segments, `--bar-mine` reserved for the shortcut.
 *
 * Mounted once by the root layout (`SiteBarSlot`); no page mounts a bar, and
 * no page reaches INTO one. The bar takes no contextual control from a page:
 * "Add {name} to my page" was the last candidate and it is gone from the
 * storefront entirely (2026-09-09); referral links live on
 * /dashboard/referrals. The one thing beside the capsule is the Sign in pill, which
 * must stay visible: it is a visitor's one action on every storefront, and
 * folding it into a burger would hide it.
 */
export function SiteBar({
  ownPage,
  signedIn = false,
  isAmbassador = false,
  wallet = null,
  lastCreator = null,
}: SiteBarProps) {
  const pathname = usePathname();
  const owning = useOwnerMode((state) => state.owning);
  const ask = useBarAsk((state) => state.ask);
  // Signing in on a page re-renders this bar rather than reloading, so the
  // stored theme is put on (or taken off) here, not only by the boot script.
  useEffect(() => syncTheme(signedIn), [signedIn]);

  // Both decided by `console-tabs.ts`; the bar only draws what it is handed.
  const tabs = sectionsFor({ ownPage: ownPage ?? null, isAmbassador, wallet });
  const shortcuts = shortcutsFor({ ownPage: ownPage ?? null, signedIn, lastCreator });

  // A sheet holds SECTIONS, so a viewer without any gets no burger at all
  // rather than one opening onto a single row. That is a client since
  // 2026-08-31 (their only page is `/`) and every signed-out visitor. Sign out
  // then rides in the capsule instead — see below.
  const hasSheet = tabs.length > 0;
  if (isRenderRoom(pathname)) return null;

  return (
    // ONE row, right-aligned at the page width and identical at every width —
    // a burger and the capsule, nothing that folds. `items-start` so
    // an open sheet hangs from the row instead of stretching it.
    <div className={tw(MENU_BAR_TOP, 'z-40')}>
      <div
        className={tw('mx-auto flex w-full items-start justify-end gap-2 px-4 sm:px-6', PAGE_WIDTH)}
      >
        <UpdateAvailableButton className="pointer-events-auto" />

        {signedIn && (
          <div className={BAR_CAPSULE}>
            {shortcuts.map((shortcut) => {
              // The fill marks the section you are in; a reselect is only the exact
              // page, so a lit pill on a subpage still navigates to its root.
              const here = isShortcutHere(pathname, shortcut.href);
              const reselect = isNavItemReselect(pathname, shortcut.href);
              return (
                <Link
                  key={shortcut.href}
                  href={shortcut.href}
                  aria-current={here ? 'page' : undefined}
                  onClick={reselect ? handleNavReselect : undefined}
                  className={tw(segmentPill(here), shortcut.face && 'gap-2 pl-1.5')}
                >
                  {shortcut.face && (
                    // eslint-disable-next-line @next/next/no-img-element -- a portrait in our bucket or a Mux clip
                    <img
                      src={shortcut.face}
                      alt=""
                      decoding="async"
                      style={{ width: SHORTCUT_FACE_PX, height: SHORTCUT_FACE_PX }}
                      className="shrink-0 rounded-full object-cover"
                    />
                  )}
                  {shortcut.label}
                </Link>
              );
            })}
            {/* A client has no sheet to keep Sign out in, so it stands where
                Sign in stands for a signed-out visitor — one capsule, one
                shape, whichever side of the door you are on. Quiet, never
                lime: the fill means "press this", and leaving is not the ask. */}
            {!hasSheet && (
              <button type="button" onClick={signOutHere} className={segmentPill(false)}>
                Sign out
              </button>
            )}
          </div>
        )}

        {/* A page with one ask makes it in Sign in's slot: the prebuilt page's
            claim IS its sign-in, and a bar offering both is two doors to one room.
            `/create` asks inside the page from its first paint (the handle, then
            the claim), so it never shows a bare Sign in. */}
        {ask ? (
          <AskPill ask={ask} signedIn={signedIn} />
        ) : (
          !signedIn && pathname !== '/create' && <SignInPill />
        )}

        {hasSheet && (
          <AccountMenu label="Menu">
            {(close) => (
              <>
                {ownPage && (
                  <MenuRow
                    href={`/${ownPage}`}
                    icon={<MenuGlyph icon={Store} />}
                    active={owning}
                    onNavigate={close}
                  >
                    My page
                  </MenuRow>
                )}
                {tabs.map((tab) => {
                  const active = isShortcutHere(pathname, tab.href);
                  return (
                    <MenuRow
                      key={tab.href}
                      href={tab.href}
                      active={active}
                      icon={<MenuGlyph icon={tab.icon} />}
                      fact={tab.fact}
                      onNavigate={(e) => {
                        close();
                        if (isNavItemReselect(pathname, tab.href)) handleNavReselect(e);
                      }}
                    >
                      {tab.label}
                    </MenuRow>
                  );
                })}
                <ThemeRow />
                <SignOutRow />
              </>
            )}
          </AccountMenu>
        )}
      </div>
    </div>
  );
}

/**
 * Signing out keeps you where you are, for everyone — one behavior, no
 * parameter. The only route that needs the session is /dashboard, and its own
 * guard already bounces a signed-out visitor to `/` (one hop, no loop).
 */
const signOutHere = () => void signOut(window.location.pathname + window.location.search);

/** The theme cycler as a row. All three glyphs are drawn and CSS shows the one
 *  the `data-mm-theme` stamp calls for — the same stamp that already decides
 *  the colors, so the row is right before any JS runs. The sheet stays open:
 *  the change is what the tap shows. */
function ThemeRow() {
  return (
    <MenuRow
      onClick={cycleTheme}
      icon={
        <span className="shrink-0" aria-hidden="true">
          <Monitor data-theme-icon="device" className="h-[18px] w-[18px]" />
          <Sun data-theme-icon="light" className="h-[18px] w-[18px]" />
          <Moon data-theme-icon="dark" className="h-[18px] w-[18px]" />
        </span>
      }
    >
      Theme
    </MenuRow>
  );
}

/** Sign out, always available to anyone signed in — same one behavior as the rail. */
function SignOutRow() {
  return (
    <MenuRow icon={<MenuGlyph icon={LogOut} />} onClick={signOutHere}>
      Sign out
    </MenuRow>
  );
}
